import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { validatePasswordResetToken, confirmSetPassword } from '~/services/auth';

type FormData = {
    email: string;
    password: string;
    password_confirmation: string;
};

const SetPassword = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [tokenEmail, setTokenEmail] = useState<string>('');
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    const search = window.location.search;
    const params = new URLSearchParams(search);
    const token = params.get('token');

    const {
        handleSubmit,
        control,
        watch,
        formState: { errors, isValid },
    } = useForm<FormData>({
        defaultValues: {
            email: '',
            password: '',
            password_confirmation: '',
        },
        mode: 'onChange',
    });

    const validateToken = useCallback(async () => {
        if (!token) {
            toast.error('No token provided');
            navigate('/login');
            return;
        }

        try {
            const data = await validatePasswordResetToken(token);

            if (data.status === 'OK') {
                if (data?.email) {
                    setTokenEmail(data.email);
                }
                setLoading(false);
            } else {
                toast.error('Invalid or expired token');
                navigate('/login');
            }
        } catch (error) {
            console.error('Error validating token:', error);
            toast.error('Error validating token');
            navigate('/login');
        }
    }, [token, navigate]);

    useEffect(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        validateToken();
    }, [validateToken]);

    const onSubmit = async (data: FormData) => {
        if (!token) return;

        setSaving(true);

        try {
            const result = await confirmSetPassword(token, data.password);

            if (!result?.token || !result?.user) {
                toast.success('Password set — please log in.');
                navigate('/login');
                return;
            }

            const userWithRoles = {
                ...result.user,
                roles: result.roles ?? [],
                person: result.person ?? null,
            };

            localStorage.setItem('token', result.token);
            localStorage.setItem('user', JSON.stringify(userWithRoles));
            if (result.expiry) {
                localStorage.setItem('expiry', result.expiry);
                const ttl = new Date(result.expiry).getTime() - Date.now();
                localStorage.setItem('sessionTTL', ttl.toString());
            }

            toast.success('Password set — welcome!');
            // Full reload so AuthProvider re-initializes from localStorage and
            // hydrates user/person/role state before the protected routes render.
            window.location.href = '/';
        } catch (error) {
            console.error('Error setting password:', error);
            const errorMessage = error instanceof Error ? error.message : 'Error setting password';
            toast.error(errorMessage);
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className='flex h-screen items-center justify-center'>
                <div className='h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent'></div>
            </div>
        );
    }

    return (
        <div className='flex h-screen flex-col items-center justify-center bg-gray-100'>
            <div className='w-full max-w-md rounded-lg bg-white p-8 shadow-lg'>
                <h2 className='mb-6 text-center text-3xl font-bold text-gray-900'>
                    Set Your Password
                </h2>
                <p className='mb-6 text-sm text-gray-600'>
                    Welcome — please set a password to access your patient
                    verifications. If your password contains capital letters,
                    include the capital letters each time you type the password.
                </p>

                <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
                    <div>
                        <label
                            htmlFor='email'
                            className='mb-2 block text-sm font-medium text-gray-700'
                        >
                            Confirm Your Email
                        </label>
                        <Controller
                            name='email'
                            control={control}
                            rules={{
                                required: 'Email is required',
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Invalid email address',
                                },
                                validate: (value: string) => {
                                    if (!tokenEmail) {
                                        return true;
                                    }
                                    const normalized = value?.trim().toLowerCase();
                                    return (
                                        normalized === tokenEmail.trim().toLowerCase() ||
                                        'Email must match the address on your invite link'
                                    );
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    {...field}
                                    id='email'
                                    type='email'
                                    className='w-full rounded-lg border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary'
                                    placeholder='Enter your email'
                                />
                            )}
                        />
                        {errors.email && (
                            <p className='mt-1 text-sm text-red-500'>
                                {errors.email.message}
                            </p>
                        )}
                    </div>

                    <div>
                        <label
                            htmlFor='password'
                            className='mb-2 block text-sm font-medium text-gray-700'
                        >
                            New Password
                        </label>
                        <div className='relative'>
                            <Controller
                                name='password'
                                control={control}
                                rules={{
                                    required: 'Password is required',
                                    minLength: {
                                        value: 8,
                                        message:
                                            'Password must be at least 8 characters',
                                    },
                                }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        id='password'
                                        type={showPassword ? 'text' : 'password'}
                                        className='w-full rounded-lg border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary'
                                        placeholder='Enter new password'
                                    />
                                )}
                            />
                            <button
                                type='button'
                                onClick={() => setShowPassword(!showPassword)}
                                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-500'
                            >
                                {showPassword ? (
                                    <svg
                                        className='h-5 w-5'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        className='h-5 w-5'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                                        />
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                                        />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {errors.password && (
                            <p className='mt-1 text-sm text-red-500'>
                                {errors.password.message}
                            </p>
                        )}
                        <p className='mt-1 text-xs text-gray-500'>
                            Must contain at least 8 characters
                        </p>
                    </div>

                    <div>
                        <label
                            htmlFor='password_confirmation'
                            className='mb-2 block text-sm font-medium text-gray-700'
                        >
                            Confirm New Password
                        </label>
                        <div className='relative'>
                            <Controller
                                name='password_confirmation'
                                control={control}
                                rules={{
                                    required: 'Please confirm your password',
                                    validate: (value) => {
                                        const password = watch('password');
                                        return (
                                            value === password ||
                                            'Passwords must match'
                                        );
                                    },
                                }}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        id='password_confirmation'
                                        type={
                                            showConfirmPassword
                                                ? 'text'
                                                : 'password'
                                        }
                                        className='w-full rounded-lg border border-stroke bg-transparent px-4 py-3 outline-none focus:border-primary'
                                        placeholder='Confirm new password'
                                    />
                                )}
                            />
                            <button
                                type='button'
                                onClick={() =>
                                    setShowConfirmPassword(!showConfirmPassword)
                                }
                                className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-500'
                            >
                                {showConfirmPassword ? (
                                    <svg
                                        className='h-5 w-5'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        className='h-5 w-5'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                                        />
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                                        />
                                    </svg>
                                )}
                            </button>
                        </div>
                        {errors.password_confirmation && (
                            <p className='mt-1 text-sm text-red-500'>
                                {errors.password_confirmation.message}
                            </p>
                        )}
                    </div>

                    <button
                        type='submit'
                        disabled={saving || !isValid}
                        className='w-full rounded-lg bg-primary px-4 py-3 font-medium text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
                    >
                        {saving ? 'Setting...' : 'Set Password'}
                    </button>

                    <button
                        type='button'
                        onClick={() => navigate('/login')}
                        className='w-full rounded-lg border border-stroke px-4 py-3 font-medium text-gray-700 transition hover:bg-gray-50'
                    >
                        Return to Login
                    </button>
                </form>
            </div>
        </div>
    );
};

export default SetPassword;
